import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import Toggle from "@cloudscape-design/components/toggle";
import Select from "@cloudscape-design/components/select";
import Box from "@cloudscape-design/components/box";

/**
 * VenueWrapper - A component that provides i18n settings to be used with venue components.
 *
 * This component exports a settings section that can be added to navigation items.
 * It manages dark mode, language selection, and logout functionality.
 *
 * @param {Function} onLogout - Callback function to handle logout
 * @param {Function} children - Render prop that receives the settings section
 */
function VenueWrapper({ onLogout, children, ...otherProps }) {
  const { t, i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState(localStorage.getItem("theme") === "dark");
  const [selectedLanguage, setSelectedLanguage] = useState({
    label: getLanguageName(i18n.language),
    value: i18n.language,
  });

  function getLanguageName(lng) {
    const names = { en: "English", es: "Español", ja: "日本語" };
    return names[lng] || lng;
  }

  useEffect(() => {
    applyMode(darkMode ? Mode.Dark : Mode.Light);
  }, [darkMode]);

  const handleDarkModeToggle = useCallback(() => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("theme", newMode ? "dark" : "light");
  }, [darkMode]);

  const handleLanguageChange = useCallback(
    ({ detail }) => {
      setSelectedLanguage(detail.selectedOption);
      i18n.changeLanguage(detail.selectedOption.value);
    },
    [i18n]
  );

  const languageOptions = useMemo(
    () => [
      { label: "English", value: "en" },
      { label: "Español", value: "es" },
      { label: "日本語", value: "ja" },
    ],
    []
  );

  // Create settings items to append to navigation
  const settingsSection = useMemo(
    () => [
      { type: "divider" },
      {
        type: "link",
        text: (
          <Box>
            <Toggle checked={darkMode} onChange={handleDarkModeToggle}>
              {t("app.common.darkMode") || "Dark Mode"}
            </Toggle>
          </Box>
        ),
        href: "#",
      },
      {
        type: "link",
        text: (
          <Box>
            <Select
              selectedOption={selectedLanguage}
              onChange={handleLanguageChange}
              options={languageOptions}
              placeholder={t("app.common.selectLanguage") || "Language"}
            />
          </Box>
        ),
        href: "#",
      },
      { type: "divider" },
      {
        type: "link",
        text: t("auth.common.logout") || "Logout",
        href: "#logout",
        external: false,
      },
    ],
    [darkMode, selectedLanguage, t, handleLanguageChange, handleDarkModeToggle, languageOptions]
  );

  // Use render prop pattern
  return children(settingsSection);
}

export default VenueWrapper;
